// data/atlasManifest.js
// Runtime atlas manifest — synchronous import, no fetch needed

import manifest from '../../assets/atlases/manifest.js';

/**
 * Look up raw atlas entry for an original image path
 * @param {string} originalPath - e.g. 'assets/icons/mining/gold_ore.png'
 * @returns {object|null} { atlas, x, y, w, h, aw, ah } or null
 */
export function getAtlasEntry(originalPath) {
    return manifest[originalPath] || null;
}

/**
 * Get computed inline background styles for rendering an atlas sprite at any display size.
 * Properly scales background-size and background-position so the correct cell
 * fills the element regardless of display dimensions.
 *
 * @param {string} originalPath - Original image path (e.g. 'assets/icons/ui_icons/fire.png')
 * @param {number} displayWidth - Desired element width in px
 * @param {number} displayHeight - Desired element height in px
 * @returns {{ atlas: string, style: string, src: string }|null}
 */
export function getAtlasBgStyle(originalPath, displayWidth, displayHeight) {
    const entry = manifest[originalPath];
    if (!entry) return null;

    const scaleX = displayWidth / entry.w;
    const scaleY = displayHeight / entry.h;

    const bgW = entry.aw * scaleX;
    const bgH = entry.ah * scaleY;
    const bgX = entry.x * scaleX;
    const bgY = entry.y * scaleY;

    return {
        atlas: entry.atlas,
        style: `background-image: url('${entry.atlas}'); background-size: ${bgW}px ${bgH}px; background-position: -${bgX}px -${bgY}px; width: ${displayWidth}px; height: ${displayHeight}px;`,
        src: originalPath
    };
}

/**
 * Get percentage-based atlas background CSS (works at ANY element size).
 * Uses CSS percentage math so the correct cell fills the element regardless of
 * width/height set by CSS context (sidebar, header, inline text, etc.).
 *
 * @param {string} originalPath - Original image path
 * @returns {{ atlas: string, bgSize: string, bgPosition: string }|null}
 */
export function getAtlasPercentBg(originalPath) {
    const entry = manifest[originalPath];
    if (!entry) return null;

    const cols = entry.aw / entry.w;
    const rows = entry.ah / entry.h;
    const col = entry.x / entry.w;
    const row = entry.y / entry.h;

    const bgSizeX = cols * 100;
    const bgSizeY = rows * 100;
    const posX = cols > 1 ? (col / (cols - 1) * 100) : 0;
    const posY = rows > 1 ? (row / (rows - 1) * 100) : 0;

    return {
        atlas: entry.atlas,
        bgSize: `${bgSizeX}% ${bgSizeY}%`,
        bgPosition: `${posX}% ${posY}%`
    };
}
